/**
 * Per-node-type system-prompt key registry.
 *
 * Drives the "Advanced: per-node prompt overrides" section inside the IVR
 * node-editor drawer. Only prompts relevant to a given node type are surfaced
 * to super-admin so they can override voice messages for this one node
 * without touching the customer / reseller / global chain.
 *
 * Derivation: the backend node handlers at
 * `backend/src/freeswitch/engine/nodes/` emit named prompts via
 * `resolvePromptAudio(ctx, slot)` / `playPrompt(ctx, key)`. This registry
 * mirrors those calls — keep in sync when a new prompt is added to a handler.
 */
import type { IvrNodeType } from '@/api/ivrNodes';

export interface NodePromptSlot {
  /** System prompt key — matches system_prompts.prompt_key. */
  key: string;
  /** Short human-readable label shown in the override UI. */
  label: string;
  /** Plain-English description of when this prompt plays. */
  description: string;
}

/**
 * Prompt keys relevant to each node type. Node types not listed here
 * expose no per-node prompt overrides (either they never play system
 * prompts, or the relevant prompt text lives entirely in a customer-
 * authored recording rather than the prompt library).
 */
export const NODE_PROMPT_KEYS: Partial<Record<IvrNodeType, NodePromptSlot[]>> = {
  voicemail: [
    { key: 'voicemail.default_greeting',   label: 'Default greeting',    description: "Plays when no custom greeting is recorded and the node's main recording is empty." },
    { key: 'voicemail.saved_confirmation', label: 'Saved confirmation',  description: "Plays after the caller's message is successfully stored." },
  ],
  listen_voicemail: [
    { key: 'voicemail.inbox_empty',        label: 'Inbox empty',         description: 'Plays when the caller has no voicemails to review.' },
    { key: 'voicemail.inbox_intro',        label: 'Inbox intro',         description: 'Plays before the first message.' },
    { key: 'voicemail.message_options',    label: 'Per-message options', description: 'DTMF menu played after each message (delete / save / skip).' },
    { key: 'voicemail.deleted',            label: 'Message deleted',     description: 'Confirmation after the caller deletes a message.' },
    { key: 'voicemail.inbox_finished',     label: 'Inbox finished',      description: 'Plays after the last message.' },
  ],

  menu: [
    { key: 'menu.timeout',                 label: 'Timeout',             description: 'Plays when no DTMF is received inside the menu window.' },
    { key: 'menu.invalid',                 label: 'Invalid selection',   description: 'Plays after a DTMF key that no branch handles.' },
  ],

  password: [
    { key: 'auth.enter_pin',               label: 'Enter PIN',           description: 'Prompt asking the caller for their PIN.' },
    { key: 'auth.pin_invalid_try_again',   label: 'PIN invalid',         description: 'Plays after a bad PIN, before the caller retries.' },
    { key: 'auth.pin_lockout',             label: 'PIN lockout',         description: 'Plays after max failed PIN attempts.' },
    { key: 'auth.caller_id_required',      label: 'Caller ID required',  description: 'Plays when the auth mode requires a caller ID.' },
    { key: 'auth.unknown_caller',          label: 'Unknown caller',      description: 'Plays when the caller is not recognised.' },
  ],

  payment: [
    { key: 'payment.enter_amount',         label: 'Enter amount',        description: 'Prompt for the payment amount.' },
    { key: 'payment.amount_out_of_range',  label: 'Amount out of range', description: 'Plays when the amount is below min or above max.' },
    { key: 'payment.recurring_period_prompt', label: 'Recurring period', description: 'Prompt for recurring period (monthly / weekly).' },
    { key: 'payment.recurring_count_prompt',  label: 'Recurring count',  description: 'Prompt for number of installments.' },
    { key: 'payment.no_gateway_configured',   label: 'No gateway',       description: 'Plays when no payment gateway is configured.' },
    { key: 'payment.confirm_amount',       label: 'Confirm amount',      description: 'Read-back of the amount before card entry.' },
    { key: 'payment.enter_card_number',    label: 'Enter card number',   description: 'Prompt for the credit-card number.' },
    { key: 'payment.enter_expiry',         label: 'Enter expiry',        description: 'Prompt for the expiry month/year.' },
    { key: 'payment.enter_cvv',            label: 'Enter CVV',           description: 'Prompt for the CVV.' },
    { key: 'payment.success',              label: 'Payment success',     description: 'Plays after a successful charge.' },
    { key: 'payment.declined',             label: 'Payment declined',    description: 'Plays after a decline.' },
  ],

  conference: [
    { key: 'conference.not_found',         label: 'Conference not found', description: 'Plays when the target conference is missing.' },
    { key: 'conference.enter_pin',         label: 'Enter conference PIN', description: 'Prompt for the conference PIN.' },
    { key: 'conference.bad_pin',           label: 'Bad conference PIN',   description: 'Plays after a wrong PIN.' },
  ],

  transfer: [
    { key: 'transfer.connecting',          label: 'Connecting',          description: 'Plays before bridging to the destination.' },
    { key: 'transfer.failed',              label: 'Transfer failed',     description: 'Plays if the bridge does not complete.' },
  ],

  enrollment: [
    { key: 'enroll.not_available',         label: 'Not available',       description: "Plays when the course is not enrollable." },
    { key: 'enroll.full',                  label: 'Enrollment full',     description: 'Plays when the course has reached its cap.' },
    { key: 'enroll.confirm',               label: 'Enrollment confirmed', description: 'Plays after a successful enrollment.' },
    { key: 'enroll.already',               label: 'Already enrolled',    description: 'Plays when the caller is already enrolled.' },
  ],

  quiz: [
    { key: 'quiz.unavailable',             label: 'Quiz unavailable',    description: 'Plays when the target quiz cannot be loaded.' },
    { key: 'quiz.no_questions',            label: 'No questions',        description: 'Plays when the quiz has no active questions.' },
    { key: 'quiz.intro',                   label: 'Quiz intro',          description: 'Plays before the first question.' },
    { key: 'quiz.passed',                  label: 'Quiz passed',         description: 'Plays when the caller passes.' },
    { key: 'quiz.failed',                  label: 'Quiz failed',         description: 'Plays when the caller fails.' },
  ],

  points: [
    { key: 'points.awarded',               label: 'Points awarded',      description: 'Plays after points are granted.' },
    { key: 'points.insufficient',          label: 'Insufficient points', description: 'Plays when the balance is too low to deduct.' },
  ],

  shopping: [
    { key: 'shopping.item_unavailable',    label: 'Item unavailable',    description: 'Plays when the targeted item is out of stock or disabled.' },
    { key: 'shopping.checkout_starting',   label: 'Checkout starting',   description: 'Plays before handing off to the payment handler.' },
  ],

  bookmark: [
    { key: 'bookmark.saved',               label: 'Bookmark saved',      description: 'Plays after a bookmark slot is written.' },
  ],
  return_to_bookmark: [
    { key: 'bookmark.which_to_resume',     label: 'Which to resume',     description: 'Prompt asking which bookmark slot to resume.' },
    { key: 'bookmark.no_bookmarks',        label: 'No bookmarks',        description: 'Plays when the caller has no bookmarks to resume from.' },
    { key: 'bookmark.resuming',            label: 'Resuming',            description: 'Plays right before jumping to the saved position.' },
  ],

  extension: [
    { key: 'extension.not_found',          label: 'Extension not found', description: 'Plays when the dialed extension is invalid.' },
  ],
};

/** True if the given node type exposes any per-node prompt overrides. */
export function hasNodePromptOverrides(nodeType: IvrNodeType): boolean {
  return (NODE_PROMPT_KEYS[nodeType]?.length ?? 0) > 0;
}
